import { useState } from "react";
import { toast } from "sonner";
import { useTranslations } from 'use-intl';
import { useFormatters } from "@financialharmony/ui";
import { Invoice } from "@/types/payments";
import { PaymentService } from "@/services/paymentService";
import { settledRefusalMessage, matchRefusalTitle } from "@/utils/errorUtils";

export function useInvoiceActions(
  invoices: Invoice[],
  setInvoices: React.Dispatch<React.SetStateAction<Invoice[]>>,
  refreshData?: () => Promise<void>
) {
  const t = useTranslations('consultantToasts');
  const { formatDate } = useFormatters();
  const [editPaymentDialogOpen, setEditPaymentDialogOpen] = useState(false);
  const [selectedInvoiceForEdit, setSelectedInvoiceForEdit] = useState<Invoice | null>(null);
  
  // Handle single row click for showing details
  const handleRowClick = (invoice: Invoice) => {
    toast(t('invoiceActions.viewDetailsTitle'), { description: t('invoiceActions.viewDetailsBody', { id: invoice.id }) });
  };
  
  // Handle marking an invoice as manually handled with specific date. `isAcknowledgment` (issue
  // #498, design plan's case c / "None of these match" escape hatch) routes to
  // paymentService.acknowledgeExternalPayment instead of the plain updatePayableStatus write —
  // used when a bank account is connected but no matching transaction was found (or confirmed
  // wrong) and the consultant ticked the acknowledgment checkbox. The existing no-bank-accounts
  // call site (case a) omits this 4th argument, so it is untouched — `isAcknowledgment` is
  // falsy and the original updatePayableStatus path runs exactly as before.
  const handleSinglePay = async (invoiceId: string, paymentDate: Date, actualSekAmount?: number, isAcknowledgment?: boolean) => {
    console.log("useInvoiceActions: handleSinglePay called", { invoiceId, paymentDate, actualSekAmount, isAcknowledgment });
    try {
      // Find the invoice in the current invoices
      const targetInvoice = invoices.find(inv => inv.id === invoiceId) || null;
      console.log("useInvoiceActions: Found invoice", targetInvoice);

      if (!targetInvoice) {
        console.error("useInvoiceActions: Invoice not found", invoiceId);
        toast.error(t('common.error'), { description: t('invoiceActions.notFound') });
        return;
      }

      console.log("useInvoiceActions: About to call PaymentService for invoice:", invoiceId);

      // Call PaymentService to update payable status
      const paymentService = new PaymentService();
      const formattedPaymentDate = paymentDate.toISOString().split('T')[0];

      const result = isAcknowledgment
        ? await paymentService.acknowledgeExternalPayment(invoiceId, formattedPaymentDate, actualSekAmount)
        : await paymentService.updatePayableStatus(invoiceId, "manually_handled", formattedPaymentDate, false, actualSekAmount);

      console.log("PaymentService success:", result);

      toast.success(t('invoiceActions.paidExternallyTitle'), {
        description: t('invoiceActions.paidExternallyBody', { id: invoiceId, date: formatDate(paymentDate) }),
      });

      // Refresh data if function provided to get updated state from server
      if (refreshData) {
        await refreshData();
      }
    } catch (error) {
      console.error("Failed to update invoice status:", error);
      toast.error(t('common.error'), { description: t('invoiceActions.updateStatusFailed') });
    }
  };

  // Confirm a manual payable→transaction match (issue #462). The client layer
  // (paymentApiClient.confirmPayableMatch) already reports failures via the shared
  // handleError() mechanism — this try/catch exists only for this hook's own UI-state
  // purposes (its own toast, and telling the caller whether to close/refresh). Do not add a
  // second call to handleError() here.
  const handleConfirmMatch = async (payableId: string, transactionId: string): Promise<boolean> => {
    try {
      const paymentService = new PaymentService();
      const result = await paymentService.confirmPayableMatch(payableId, transactionId);
      console.log("useInvoiceActions: confirmPayableMatch succeeded", result);

      toast.success(t('invoiceActions.matchConfirmedTitle'), {
        description: t('invoiceActions.matchConfirmedBody', { id: payableId }),
      });

      if (refreshData) {
        await refreshData();
      }
      return true;
    } catch (error) {
      console.error("useInvoiceActions: Failed to confirm payable match:", error);
      // FIN-968 (widened by FIN-1023 D7/AC-13): a permanent refusal is named, never "please try again".
      const settled = settledRefusalMessage(error);
      toast.error(settled ? matchRefusalTitle(error) ?? t('matching.alreadyMatched') : t('common.error'), {
        description: settled ?? t('invoiceActions.confirmMatchFailed'),
      });
      return false;
    }
  };

  // Handle single cancel
  const handleSingleCancel = (invoiceId: string) => {
    toast.success(t('invoiceActions.cancelledTitle'), { description: t('invoiceActions.cancelledBody', { id: invoiceId }) });
  };

  // Handle single export
  const handleSingleExport = (invoiceId: string) => {
    toast.success(t('invoiceActions.exportedTitle'), { description: t('invoiceActions.exportedBody', { id: invoiceId }) });
  };

  // Handle download invoice
  const handleDownloadInvoice = (_invoiceId: string, fileName: string) => {
    toast.success(t('invoiceActions.downloadedTitle'), { description: t('invoiceActions.downloadedBody', { fileName }) });
  };

  // Handle edit payment - now opens the dialog
  const handleEditPayment = (invoiceId: string) => {
    const foundInvoice = invoices.find(inv => inv.id === invoiceId);
    if (foundInvoice) {
      setSelectedInvoiceForEdit(foundInvoice);
      setEditPaymentDialogOpen(true);
    }
  };

  // Handle edit vendor
  const handleEditVendor = (invoiceId: string) => {
    toast(t('invoiceActions.editVendorTitle'), {
      description: t('invoiceActions.editVendorBody', { id: invoiceId }),
    });
  };

  // Handle edit invoice
  const handleEditInvoice = (invoiceId: string) => {
    toast(t('invoiceActions.editInvoiceTitle'), {
      description: t('invoiceActions.editInvoiceBody', { id: invoiceId }),
    });
  };

  // Handle successful payment update
  const handlePaymentUpdateSuccess = async () => {
    setEditPaymentDialogOpen(false);
    setSelectedInvoiceForEdit(null);
    toast.success(t('invoiceActions.paymentUpdatedTitle'), { description: t('invoiceActions.paymentUpdatedBody') });
    
    // Refresh the data if refreshData function is provided
    if (refreshData) {
      await refreshData();
    }
  };

  return {
    handleRowClick,
    handleSinglePay,
    handleConfirmMatch,
    handleSingleCancel,
    handleSingleExport,
    handleDownloadInvoice,
    handleEditPayment,
    handleEditVendor,
    handleEditInvoice,
    editPaymentDialogOpen,
    setEditPaymentDialogOpen,
    selectedInvoiceForEdit,
    handlePaymentUpdateSuccess
  };
}
